
import { NightlyRate, MinimumStay, PricingSettings, PromoCode } from "@/types/supabase";

// Compare calendar days as local 'yyyy-MM-dd' strings to avoid UTC/local shifts
const toDayKey = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const dbDayKey = (v: string): string => String(v).slice(0, 10);

interface BookingDetails {
  checkIn: Date;
  checkOut: Date;
  nights: number;
  subtotal: number;
  cleaningFee: number;
  touristTax: number;
  total: number;
  discount: number;
}

export const calculateBookingDetails = (
  checkIn: Date | undefined,
  checkOut: Date | undefined,
  guestsCount: number,
  nightlyRates: NightlyRate[] | undefined,
  pricingSettings: PricingSettings | undefined,
  appliedPromoCode: PromoCode | null
): BookingDetails | null => {
  if (!checkIn || !checkOut || !guestsCount) return null;
  
  const nights = Math.round((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
  
  let totalNightlyRate = 0;
  
  if (nightlyRates && nightlyRates.length > 0) {
    const currentDate = new Date(checkIn.getTime());
    
    for (let i = 0; i < nights; i++) {
      const dateToCheck = new Date(currentDate.getTime());
      dateToCheck.setDate(dateToCheck.getDate() + i);
      
      const applicableRate = nightlyRates.find(rate => {
        const day = toDayKey(dateToCheck);
        return day >= dbDayKey(rate.start_date) && day <= dbDayKey(rate.end_date);
      });
      
      if (applicableRate) {
        totalNightlyRate += Number(applicableRate.price_per_night);
      } else {
        totalNightlyRate += 250;
      }
    }
  } else {
    const baseRate = 250;
    const isHighSeason = checkIn.getMonth() >= 5 && checkIn.getMonth() <= 7;
    const nightlyRate = isHighSeason ? baseRate * 1.5 : baseRate;
    totalNightlyRate = nightlyRate * nights;
  }
  
  const cleaningFee = pricingSettings?.cleaning_fee || 120;
  const touristTaxPerPerson = pricingSettings?.tourist_tax_per_person || 0;
  const touristTax = touristTaxPerPerson * guestsCount * nights;
  
  const subtotal = totalNightlyRate;
  const total = subtotal + cleaningFee + touristTax;
  
  let finalTotal = total;
  let discount = 0;
  
  if (appliedPromoCode) {
    if (appliedPromoCode.discount_type === 'percentage') {
      discount = total * (appliedPromoCode.discount_value / 100);
    } else {
      discount = appliedPromoCode.discount_value;
    }
    finalTotal = total - discount;
  }
  
  return {
    checkIn,
    checkOut,
    nights,
    subtotal,
    cleaningFee,
    touristTax,
    total: finalTotal,
    discount
  };
};

export const validateMinimumStay = (
  checkIn: Date | undefined,
  checkOut: Date | undefined,
  minimumStays: MinimumStay[] | undefined
): { isValid: boolean; minimumNights?: number } => {
  if (!checkIn || !checkOut || !minimumStays?.length) return { isValid: true };
  
  const nights = Math.round((checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60 * 24));
  
  const applicableStay = minimumStays.find(stay => {
    const day = toDayKey(checkIn);
    return day >= dbDayKey(stay.start_date) && day <= dbDayKey(stay.end_date);
  });
  
  if (applicableStay && nights < applicableStay.minimum_nights) {
    return { isValid: false, minimumNights: applicableStay.minimum_nights };
  }
  
  return { isValid: true };
};
